import { Server, Fingerprint, FolderKanban, ListTodo, ChartPie, Activity, Headset } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { useITProjects } from '../widgets/it/hooks/useITProjects';
import { useITTasks } from '../widgets/it/hooks/useITTasks';
import { useDevices } from '@/features/attendance/hooks/useBiometricAdmin';
import { DashboardHero } from '../infrastructure/widgets/DashboardHero';
import { DonutChart } from '../infrastructure/widgets/DonutChart';
import { SectionCard, SimpleList, SimpleListItem } from '../infrastructure/widgets/DashboardKit';

const DEVICE_COLOR = { online: '#12b76a', offline: '#f04438' };
const TASK_PRIORITY_COLOR: Record<string, string> = { critical: '#f04438', high: '#f79009', medium: '#5b45d6', low: '#667085' };

export function ITAdminDashboard() {
  const user = useUser();
  const { data: projects = [] } = useITProjects();
  const { data: tasks = [] } = useITTasks();
  const { data: devices = [] } = useDevices();

  const activeProjects = projects.filter((p) => p.status !== 'done' && p.status !== 'completed');
  const openTasks = tasks.filter((t) => t.status !== 'done');
  const onlineDevices = devices.filter((d) => d.online);
  const offlineDevices = devices.filter((d) => !d.online);

  const deviceSegments = devices.length > 0
    ? [
        { label: 'Online', value: onlineDevices.length, color: DEVICE_COLOR.online },
        { label: 'Offline', value: offlineDevices.length, color: DEVICE_COLOR.offline },
      ].filter((s) => s.value > 0)
    : [];

  const taskItems: SimpleListItem[] = [...openTasks]
    .sort((a, b) => (a.due_date && b.due_date ? new Date(a.due_date).getTime() - new Date(b.due_date).getTime() : 0))
    .slice(0, 6)
    .map((t) => ({
      icon: ListTodo,
      title: t.title,
      meta: t.project_name ? `${t.project_name}${t.due_date ? ` · Due ${new Date(t.due_date).toLocaleDateString()}` : ''}` : (t.due_date ? `Due ${new Date(t.due_date).toLocaleDateString()}` : 'No due date'),
      tag: t.priority,
      tagHex: TASK_PRIORITY_COLOR[t.priority] || '#5b45d6',
    }));

  const deviceItems: SimpleListItem[] = offlineDevices.slice(0, 6).map((d) => ({
    icon: Fingerprint,
    title: d.device_name || d.name || d.serial_number,
    meta: d.location || d.serial_number,
    tag: 'Offline',
    tagHex: DEVICE_COLOR.offline,
  }));

  return (
    <div className="mx-auto max-w-[1440px]">
      <DashboardHero
        badge="IT · Infrastructure"
        badgeIcon={Server}
        title={`Welcome, ${user?.displayName || 'there'}`}
        subtitle="System projects, tasks and biometric device fleet status."
        heroStats={[
          // One row of figures (the header strip and a card row repeated them).
          { label: 'Active projects', value: String(activeProjects.length), sub: `of ${projects.length} total`, icon: FolderKanban, tone: 'primary' },
          { label: 'Open tasks', value: String(openTasks.length), sub: `${tasks.length} total`, icon: ListTodo, tone: 'info' },
          { label: 'Devices online', value: `${onlineDevices.length}/${devices.length}`, sub: 'biometric fleet', icon: Fingerprint, tone: 'success' },
          { label: 'Devices offline', value: String(offlineDevices.length), sub: offlineDevices.length ? 'need attention' : 'all reporting', icon: Activity, tone: offlineDevices.length ? 'danger' : 'neutral' },
        ]}
      />

      <div className="grid grid-cols-12 gap-4">
        {deviceSegments.length > 0 && (
          <SectionCard icon={ChartPie} title="Device Health" subtitle="Biometric terminal status" span={4}>
            <DonutChart segments={deviceSegments} centerValue={String(devices.length)} centerLabel="Devices" />
          </SectionCard>
        )}
        <SectionCard icon={ListTodo} title="IT Tasks" subtitle={`${openTasks.length} open, by due date`} span={deviceSegments.length > 0 ? 8 : 12}>
          <SimpleList items={taskItems} />
        </SectionCard>
        {deviceItems.length > 0 && (
          <SectionCard icon={Headset} title="Offline Devices" subtitle={`${offlineDevices.length} need attention`} span={12}>
            <SimpleList items={deviceItems} />
          </SectionCard>
        )}
      </div>
    </div>
  );
}
